import { useMediaQuery } from "@mui/material";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  getCompanySizes,
  getStateRegions,
  updateCompanyFilter,
} from "../../../redux/actions/company_action";
import {
  CurrentSearchContainer,
  CurrentSearchText,
  DividerStyled,
  FilterChip,
  FilterCount,
  FilterListContainer,
  FilterOption,
  FilterOptionContainer,
  FilterTitleContainer,
  Wrapper,
} from "./styles";

export default function CompanyFilter(props) {
  const { serverFilterParams } = props;

  const router = useRouter();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const isMobile = useMediaQuery("(max-width: 640px)");

  const fetchingCompanies = useSelector(
    (state) => state.companies.fetchingCompanyList
  );
  const companyFilter = useSelector((state) => state.companies.companyFilter);
  const keywordSearch = companyFilter?.keyword;

  const industriesList = useSelector((state) => state.companies.industries);
  const stateRegionsList = useSelector((state) => state.companies.stateRegions);
  const companySizesList = useSelector((state) => state.companies.companySizes);

  const categoryList = [
    { title: t("companies.industry"), id: "industry_ids" },
    { title: t("companies.location"), id: "state_regions" },
    { title: t("companies.company_size"), id: "company_sizes" },
  ];

  const [selectedCategory, setSelectedCategory] = useState(categoryList[0]);
  const [selectedFilter, setSelectedFilter] = useState(
    Object.keys(serverFilterParams).length > 0 ? serverFilterParams : {}
  );

  const selectedFilterList =
    selectedCategory.id === "industry_ids"
      ? industriesList
      : selectedCategory.id === "state_regions"
      ? stateRegionsList
      : companySizesList;

  const getIndustryLabel = (industry) => {
    if (!industry) return "";
    const fallback = industry?.slug || "";
    const keyBase = industry?.title || fallback;
    if (!keyBase) return fallback;
    const key = keyBase
      .toString()
      .toLowerCase()
      .replace(/&/g, "and")
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
    return t(`companies.industry_labels.${key}`, { defaultValue: fallback });
  };

  const getStateRegionLabel = (stateRegion) => {
    if (!stateRegion) return "";
    const fallback = stateRegion?.slug || "";
    const keyBase = stateRegion?.state || fallback;
    if (!keyBase) return fallback;
    const key = keyBase
      .toString()
      .toLowerCase()
      .replace(/&/g, "and")
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
    return t(`companies.state_region_labels.${key}`, {
      defaultValue: fallback,
    });
  };

  const getCompanySizeLabel = (companySize) => {
    if (!companySize) return "";
    const fallback = companySize?.slug || "";
    const keyBase = companySize?.title;
    if (!keyBase) return fallback;
    const key = keyBase
      .toString()
      .toLowerCase()
      .replace(/&/g, "and")
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
    return t(`companies.company_size_labels.${key}`, {
      defaultValue: fallback,
    });
  };

  function getFilterCount(categoryId) {
    switch (categoryId) {
      case "industry_ids":
        return selectedFilter["industry_ids"]?.length;
      case "state_regions":
        return selectedFilter["state_regions"]?.length;
      case "company_sizes":
        return selectedFilter["company_sizes"]?.length;
    }
  }

  function checkSelectedFilter(filterParams) {
    if (selectedFilter[selectedCategory.id] === undefined) {
      return false;
    }

    switch (selectedCategory.id) {
      case "industry_ids": {
        const filterId = parseInt(filterParams);
        return selectedFilter[selectedCategory.id].includes(filterId);
      }
      case "state_regions": {
        return selectedFilter[selectedCategory.id].includes(filterParams);
      }
      case "company_sizes": {
        const filterId = parseInt(filterParams);
        return selectedFilter[selectedCategory.id].includes(filterId);
      }
      default:
        return false;
    }
  }

  function handleFilterTypeClick(filter) {
    setSelectedCategory(filter);
  }

  function handleAllFilterClick() {
    const newFilter = { ...selectedFilter };
    newFilter[selectedCategory.id] = [];

    setSelectedFilter(newFilter);

    const currentCompanyFilter = {
      ...(keywordSearch && { keyword: keywordSearch ?? "" }),
      industry_ids:
        newFilter["industry_ids"]?.length > 0 ? newFilter["industry_ids"] : [],
      state_regions:
        newFilter["state_regions"]?.length > 0
          ? newFilter["state_regions"]
          : [],
      company_sizes:
        newFilter["company_sizes"]?.length > 0
          ? newFilter["company_sizes"]
          : [],
    };

    router.push(
      {
        pathname: "/companies",
        query: { ...currentCompanyFilter },
        scroll: false,
      },
      undefined,
      { shallow: true }
    );

    dispatch(updateCompanyFilter(currentCompanyFilter));
  }

  function handleFilterChipClick(filterParams) {
    let filterId;
    if (selectedCategory.id === "state_regions") {
      filterId = filterParams;
    } else {
      filterId = parseInt(filterParams);
    }
    const tempFilter = { ...selectedFilter };

    if (tempFilter[selectedCategory.id]?.includes(filterId)) {
      tempFilter[selectedCategory.id] = tempFilter[selectedCategory.id].filter(
        (item) => item !== filterId
      );
    } else if (selectedFilter[selectedCategory.id] === undefined) {
      tempFilter[selectedCategory.id] = [filterId];
    } else {
      tempFilter[selectedCategory.id] = [
        ...tempFilter[selectedCategory.id],
        filterId,
      ];
    }

    setSelectedFilter(tempFilter);

    const currentCompanyFilter = {
      ...(keywordSearch && { keyword: keywordSearch ?? "" }),
      industry_ids:
        tempFilter["industry_ids"]?.length > 0
          ? tempFilter["industry_ids"]
          : [],
      state_regions:
        tempFilter["state_regions"]?.length > 0
          ? tempFilter["state_regions"]
          : [],
      company_sizes:
        tempFilter["company_sizes"]?.length > 0
          ? tempFilter["company_sizes"]
          : [],
    };

    router.push(
      {
        pathname: "/companies",
        query: {
          ...currentCompanyFilter,
        },
        scroll: false,
      },
      undefined,
      { shallow: true }
    );

    dispatch(updateCompanyFilter(currentCompanyFilter));
  }

  useEffect(() => {
    // dispatch(getIndustries());
    dispatch(getCompanySizes());
    dispatch(getStateRegions());

    dispatch(updateCompanyFilter(serverFilterParams));
  }, [dispatch]);

  return (
    <Wrapper>
      {keywordSearch && (
        <CurrentSearchContainer>
          <Trans
            i18nKey="companies.search_results_for"
            values={{ keyword: keywordSearch }}
            components={[<CurrentSearchText />]}
          />
        </CurrentSearchContainer>
      )}

      <FilterTitleContainer>
        {t("companies.discover_by")}
        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "0.75rem", flexWrap: "nowrap" }}>
          {categoryList.map((category, index) => (
            <FilterOptionContainer key={index}>
              {index > 0 && !isMobile && (
                <DividerStyled orientation="vertical" flexItem />
              )}
              <FilterOption
                selected={selectedCategory.id === category.id}
                disabled={fetchingCompanies}
                onClick={() => handleFilterTypeClick(category)}
              >
                {category.title}
              </FilterOption>
              {getFilterCount(category.id) > 0 && (
                <FilterCount>{getFilterCount(category.id)}</FilterCount>
              )}
            </FilterOptionContainer>
          ))}
        </div>
      </FilterTitleContainer>

      <FilterListContainer>
        <FilterChip
          selected={
            selectedFilter[selectedCategory.id]?.length === 0 ||
            selectedFilter[selectedCategory.id] === undefined
          }
          disabled={fetchingCompanies}
          onClick={handleAllFilterClick}
        >
          {t("companies.all")}
        </FilterChip>
        {selectedFilterList?.map((item, index) => (
          <FilterChip
            key={index}
            selected={checkSelectedFilter(item?.state || item?.id)}
            disabled={fetchingCompanies}
            onClick={() => handleFilterChipClick(item?.state || item?.id)}
          >
            {selectedCategory.id === "industry_ids"
              ? getIndustryLabel(item)
              : selectedCategory.id === "state_regions"
              ? getStateRegionLabel(item)
              : selectedCategory.id === "company_sizes"
              ? getCompanySizeLabel(item)
              : item.title || item.state}
          </FilterChip>
        ))}
      </FilterListContainer>
    </Wrapper>
  );
}
